import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { ModuleLayout } from "../../../layouts";
import { deleteCompanyApi, getCompanyByIdApi } from "../../../DAL";
import { enqueueSnackbar } from "notistack";
import { ROUTES_CONSTANTS, QUERY_CONSTANTS } from "../../../constants";
import { useAuth } from "../../../context";
import { Loader } from "../../../components";

// ─── Group modules by category ────────────────────────────────────────────────
const groupByCategory = (modules) => {
  return modules.reduce((acc, mod) => {
    const cat = mod.module?.code;
    if (!cat) return acc;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(mod);
    return acc;
  }, {});
};

// ─── Main component ───────────────────────────────────────────────────────────
const CompanyDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: company, isLoading, isError, error } = useQuery({
    queryKey: [QUERY_CONSTANTS.COMPANY, currentUser?.company?.id],
    queryFn: async () => {
      const response = await getCompanyByIdApi(currentUser.company.id);
      return response.data.company;
    },
    enabled: !!currentUser?.company?.id,
  });

  const groupedModules = company?.modules?.length
    ? groupByCategory(company.modules)
    : {};

  const { mutate: deleteCompany, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteCompanyApi(company.id),
    onSuccess: (response) => {
      enqueueSnackbar(response?.message, { variant: "success" });
      queryClient.invalidateQueries({ queryKey: [QUERY_CONSTANTS.CURRENT_USER] });
      navigate(ROUTES_CONSTANTS.COMPANY_MANAGEMENT);
    },
  });

  if (isLoading) {
    return (
      <ModuleLayout
        title={t("companyManagement.title")}
        onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_MANAGEMENT)}
      >
        <div className="flex items-center justify-center h-full">
          <Loader fullPage={false} />
        </div>
      </ModuleLayout>
    );
  }

  if (isError) throw error;

  return (
    <>
      <ModuleLayout
        title={t("companyManagement.title")}
        onBack={() => navigate(ROUTES_CONSTANTS.COMPANY_MANAGEMENT)}
      >
        <div className="space-y-4">
          {/* ── Company Header Card ──────────────────────────────────────────── */}
          <div className="bg-[#f5f5f5] rounded-xl px-6 py-5 flex items-center gap-5 relative shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)]">
            {/* Logo */}
            <div className="w-20 h-20 rounded-full bg-black flex items-center justify-center shrink-0 overflow-hidden border-2 border-[#333]">
              <span className="text-white text-xs font-bold leading-tight px-1 text-center">
                {company.company_name
                  .split(" ")
                  .slice(0, 3)
                  .map((w) => w[0])
                  .join("")}
              </span>
            </div>

            {/* Company name + registration number */}
            <div>
              <h2 className="text-2xl font-semibold text-[#333] uppercase tracking-wide">
                {company.company_name}
              </h2>
              <p className="text-sm text-[#999] mt-0.5">
                {company.registration_number}
              </p>
            </div>

            {/* Action buttons */}
            <div className="absolute top-5 right-5 flex items-center gap-2">
              <button
                onClick={() => navigate(ROUTES_CONSTANTS.UPDATE_COMPANY)}
                className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-600 transition cursor-pointer"
                title={t("companyDetail.editBtnTitle")}
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
              {/* <button
                onClick={() => setConfirmDelete(true)}
                className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-red-500 transition cursor-pointer"
                title={t("companyDetail.deleteBtnTitle")}
              >
                <FiTrash2 className="w-4 h-4" />
              </button> */}
            </div>
          </div>

          {/* ── Company Description Card ─────────────────────────────────────── */}
          <div className="bg-white rounded-xl px-6 py-5 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)]">
            <h3 className="text-sm font-bold text-[#333] uppercase tracking-wide mb-3">
              {t("companyDetail.sectionDescription")}
            </h3>
            <p className="text-sm text-[#999] leading-relaxed">
              {company.description}
            </p>
          </div>

          {/* ── Assigned Module Card ─────────────────────────────────────────── */}
          <div className="bg-white rounded-xl px-6 py-5 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-[#333] uppercase tracking-wide">
                {t("companyDetail.sectionModules")}
              </h3>
              <button className="text-sm font-medium text-primary-500 hover:text-primary-600 transition cursor-pointer">
                {t("companyDetail.editModules")}
              </button>
            </div>

            {Object.keys(groupedModules).length > 0 ? (
              <div className="flex flex-wrap gap-12">
                {Object.entries(groupedModules).map(([category, mods]) => (
                  <div key={category} className="min-w-35">
                    <p className="text-sm text-[#999] mb-2">{category}</p>
                    <ul className="space-y-1.5">
                      {mods.map((mod) => (
                        <li
                          key={mod.id}
                          className="text-sm font-semibold text-[#333]"
                        >
                          {mod.module?.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#999]">{t("companyDetail.noModules")}</p>
            )}
          </div>
        </div>
      </ModuleLayout>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg px-8 py-6 w-80 flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <FiTrash2 className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-[#333]">
              {t("companyDetail.deleteModalTitle")}
            </h3>
            <p className="text-sm text-[#999] text-center">
              {t("companyDetail.deleteConfirmPre")}{" "}
              <span className="font-medium text-[#333]">
                {company.company_name}
              </span>
              {t("companyDetail.deleteConfirmPost")}
            </p>
            <div className="flex gap-3 w-full mt-1">
              <button
                onClick={() => setConfirmDelete(false)}
                disabled={isDeleting}
                className="flex-1 py-2 rounded-lg border border-[#E3E3E3] text-sm font-medium text-[#333] hover:bg-gray-50 transition disabled:opacity-50 cursor-pointer"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => deleteCompany()}
                disabled={isDeleting}
                className="flex-1 py-2 rounded-lg bg-red-500 text-sm font-medium text-white hover:bg-red-600 transition disabled:opacity-50 cursor-pointer"
              >
                {isDeleting ? t("common.deleting") : t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyDetail;
